import { $log } from './debug';
import { $location } from './location';
import { yLoadAsyncScript } from './util';

declare global {
    interface Window {
        dataLayer?: any[];
        fbq(a: any, b?: any): any;
        analytics?: SegmentAnalytics.AnalyticsJS;
    }
}

export namespace $analytics {
    let have_ga = false;
    let have_mixpanel = false;
    let have_gtm = false;
    let have_segment = false;

    let times: { [key: string]: number } = {};
    let identified = false;
    let current_page: string;
    let load_time: number;
    let ready_time: number;
    let pending_segment_id: string | undefined;

    interface YUTMParameters {
        name?: string;
        source: string;
        medium?: string;
        campaign?: string;
        term?: string;
        content?: string;
    }

    let utm_parameters: YUTMParameters | undefined;

    export function initGA(id: string) {
        if(!have_ga && 'ga' in window) {
            ga('create', id, 'auto');
            ga('set', 'transport', 'beacon');
            have_ga = true;
        }
    }

    export function initGTM(id: string) {
        if(!have_gtm) {
            window.dataLayer = [{ 'gtm.start': Date.now(), event: 'gtm.js' }];
            have_gtm = true;
        }

        return yLoadAsyncScript('https://www.googletagmanager.com/gtm.js?id=' + id);
    }

    export function initFBPixel(id: string) {
        if(window.fbq) {
            window.fbq('init', id);
            window.fbq('track', 'PageView');
        }
    }

    export function preInitSegment(id: string) {
        const query = $location.getCurrentQuery();

        if(query.utm_source) {
            utm_parameters = {
                name: query.utm_campaign,
                source: query.utm_source,
                medium: query.utm_medium,
                campaign: query.utm_campaign,
                term: query.utm_term,
                content: query.utm_content
            };
        }

        $log('app', 'parameters', utm_parameters);

        have_segment = true;
        pending_segment_id = id;
    }

    export function finishInitSegment() {
        if(pending_segment_id) {
            analytics?.load?.(pending_segment_id);
            pending_segment_id = undefined;
        }
    }

    export function initSegment(id: string) {
        const query = $location.getCurrentQuery();

        if(query.utm_source) {
            utm_parameters = {
                name: query.utm_campaign,
                source: query.utm_source,
                medium: query.utm_medium,
                campaign: query.utm_campaign,
                term: query.utm_term,
                content: query.utm_content
            };
        }

        $log('app', 'parameters', utm_parameters);

        if(!have_segment && window.analytics) {
            analytics.load(id);
            have_segment = true;
        }
    }

    export function initMixpanel(id: string) {
        if(!have_mixpanel && 'mixpanel' in window) {
            mixpanel.init(id, { track_pageview: false, persistence: 'localStorage' });
            have_mixpanel = true;
        }
    }

    export function identify(id: string, profile: { [index: string]: any }, properties?: { [index: string]: any }, event?: string, params?: { [index: string]: any },
        alias?: boolean, intercom_user_hash?: string) {
        identified = true;

        if(have_segment) {
            if(alias)
                analytics.alias(id);
            const options = intercom_user_hash ? { integrations: { Intercom: { user_hash: intercom_user_hash } } } : undefined;
            analytics.identify(id, profile, options);

            if(event) {
                analytics.track(event, params);
            }
        }

        if(have_mixpanel) {
            if(alias)
                mixpanel.alias(id);
            mixpanel.identify(id);
            mixpanel.people.set(profile);

            properties && mixpanel.register(properties);

            if(event) {
                params = params || {};

                if(load_time) params['Load time'] = load_time;
                if(ready_time) params['Ready time'] = ready_time;
                if(window.performance && window.performance.now)
                    params['Login time'] = Math.round(window.performance.now());

                mixpanel.track(event, params);
            }
        }
    }

    export function set(properties: { [index: string]: any }) {
        if(!identified) {
            $log('analytics', 'Trying to set mixpanel people property without calling identify first');
            return;
        }

        if(!have_mixpanel) return;

        mixpanel.people.set(properties);
    }

    export function track(name: string, params?: { [index: string]: any }) {
        if(have_segment) {
            analytics.track(name, params);
        }

        if(have_mixpanel) {
            mixpanel.track(name, params);
        }
    }

    export function trackSearch(query: string) {
        if('ga' in window) {
            ga('send', 'pageview', '/search?q=' + encodeURIComponent(query));
        }
    }

    export function trackPage(page?: string) {
        if(!page)
            page = window.location.href;
        if(page === current_page) return;
        current_page = page;

        if(have_mixpanel) {
            mixpanel.track('Open page', { 'Page': page });
            mixpanel.register({ 'Current page': page });
        }

        if(have_segment) {
            if(utm_parameters)
                analytics.page(window.location.pathname, { url: window.location.href, path: window.location.pathname }, { context: { campaign: utm_parameters } });
            else
                analytics.page(window.location.pathname, { url: window.location.href, path: window.location.pathname });
        }

        if(have_ga) {
            if('ga' in window) {
                ga('send', 'pageview');
            }
        }
    }

    export function trackPerformance(_event: string) {
        if(!have_ga) return;

        if(window.performance && window.performance.now)
            ga('send', 'timing', 'Performance', name, Math.round(window.performance.now()));
    }

    export function setProperties(properties: { [index: string]: any }) {
        if(!have_mixpanel) return;

        mixpanel.register(properties);
    }

    export function unsetProperty(property: string) {
        if(!have_mixpanel) return;

        mixpanel.unregister(property);
    }

    export function trackException(description: string) {
        if(have_mixpanel) {
            mixpanel.track('Exception', { 'Description': description });
        }

        if(have_ga) {
            ga('send', 'exception', { exDescription: description });
        }
    }

    export function trackWithClientID(id: string) {
        if(!have_ga) return;

        ga('create', id, 'auto', 'clientTracker');
        ga('clientTracker.send', 'pageview');
    }

    if(window.performance && window.performance.now)
        load_time = Math.round(window.performance.now());

    setTimeout(() => {
        if(window.performance && window.performance.now)
            ready_time = Math.round(window.performance.now());
        trackPerformance('Application loaded');
        times['loaded'] = Date.now();
    });

    trackPerformance('Script loaded');
}
